import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, CheckCircle, XCircle, Truck, Package } from 'lucide-react';
import { useOrdemCompra, useOrdemCompraDetalhe } from '@/features/compras/hooks/useOrdemCompra';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import type { OrdemCompraItemRequest } from '@/features/compras/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

interface ItemLine extends OrdemCompraItemRequest {
  _key: number;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  Rascunho:            { label: 'Rascunho',              className: 'bg-gray-500/10 text-gray-600'    },
  Pendente:            { label: 'Pendente',              className: 'bg-yellow-500/10 text-yellow-600' },
  EmAprovacao:         { label: 'Em Aprovação',          className: 'bg-blue-500/10 text-blue-600'    },
  Aprovada:            { label: 'Aprovada',              className: 'bg-green-500/10 text-green-600'  },
  EnviadaFornecedor:   { label: 'Enviada ao Fornecedor', className: 'bg-cyan-500/10 text-cyan-600'    },
  ParcialmenteRecebida:{ label: 'Rec. Parcial',          className: 'bg-orange-500/10 text-orange-600' },
  TotalmenteRecebida:  { label: 'Recebida',              className: 'bg-purple-500/10 text-purple-600' },
  Cancelada:           { label: 'Cancelada',             className: 'bg-red-500/10 text-red-600'      },
};

const OrdemCompraForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdicao = Boolean(id && id !== 'nova');
  const idNum = isEdicao ? Number(id) : 0;

  const {
    criarOrdem, atualizarOrdem, aprovarOrdem, enviarFornecedor, isCreating, isUpdating, isAprovando, isEnviando,
  } = useOrdemCompra();
  const { data: detalhe } = useOrdemCompraDetalhe(idNum);

  const [fornecedorId,      setFornecedorId]      = useState(0);
  const [dtEntregaPrevista, setDtEntregaPrevista] = useState('');
  const [prazoPagamento,    setPrazoPagamento]    = useState(30);
  const [frete,             setFrete]             = useState(0);
  const [desconto,          setDesconto]          = useState(0);
  const [outrasDespesas,    setOutrasDespesas]    = useState(0);
  const [observacao,        setObservacao]        = useState('');
  const [itens,             setItens]             = useState<ItemLine[]>([]);
  const [nextKey,           setNextKey]           = useState(1);

  useEffect(() => {
    if (detalhe) {
      setFornecedorId(detalhe.fornecedorId);
      setDtEntregaPrevista(detalhe.dtEntregaPrevista?.substring(0, 10) ?? '');
      setPrazoPagamento(detalhe.prazoPagamentoDias);
      setFrete(detalhe.frete);
      setDesconto(detalhe.desconto);
      setOutrasDespesas(detalhe.outrasDespesas);
      setObservacao(detalhe.observacao ?? '');
      const linhas: ItemLine[] = detalhe.itens.map((i, idx) => ({
        _key: idx + 1,
        produtoId: i.produtoId,
        descricaoComplementar: i.descricaoComplementar,
        quantidade: i.quantidade,
        unidadeId: i.unidadeId,
        custoUnitario: i.custoUnitario,
        descontoItem: i.descontoItem,
        localEstoqueId: i.localEstoqueId,
      }));
      setItens(linhas);
      setNextKey(linhas.length + 1);
    }
  }, [detalhe]);

  const addItem = () => {
    setItens(prev => [...prev, { _key: nextKey, produtoId: 0, quantidade: 1, custoUnitario: 0, descontoItem: 0 }]);
    setNextKey(k => k + 1);
  };

  const removeItem = (key: number) => setItens(prev => prev.filter(i => i._key !== key));

  const updateItem = (key: number, field: keyof OrdemCompraItemRequest, value: unknown) => {
    setItens(prev => prev.map(i => i._key === key ? { ...i, [field]: value } : i));
  };

  const valorProdutos = itens.reduce((s, i) => s + i.quantidade * i.custoUnitario - i.descontoItem, 0);
  const valorTotal    = valorProdutos + frete + outrasDespesas - desconto;

  const handleSalvar = async () => {
    const payload = {
      fornecedorId,
      dtEntregaPrevista: dtEntregaPrevista || undefined,
      prazoPagamentoDias: prazoPagamento,
      frete,
      desconto,
      outrasDespesas,
      observacao,
      itens: itens.map(({ _key: _k, ...rest }) => rest),
    };
    if (isEdicao) {
      await atualizarOrdem({ id: idNum, data: payload });
    } else {
      const nova = await criarOrdem(payload);
      navigate(`/compras/ordens/${nova.id}`);
      return;
    }
  };

  const podeEditar = !isEdicao || detalhe?.status === 'Rascunho' || detalhe?.status === 'Pendente';
  const isSaving = isCreating || isUpdating;
  const status = detalhe?.status;
  const statusCfg = status ? statusConfig[status] : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/compras/ordens')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdicao ? `PO #${detalhe?.numero ?? ''}` : 'Nova Ordem de Compra'}
            </h1>
            {statusCfg && <Badge className={statusCfg.className}>{statusCfg.label}</Badge>}
          </div>
        </div>

        <div className="flex gap-2">
          {status === 'Pendente' || status === 'EmAprovacao' ? (
            <>
              <Button variant="outline" className="gap-2 text-green-600 border-green-600"
                disabled={isAprovando}
                onClick={() => aprovarOrdem({ id: idNum, request: { aprovado: true } })}>
                <CheckCircle className="h-4 w-4" />Aprovar
              </Button>
              <Button variant="outline" className="gap-2 text-red-600 border-red-600"
                disabled={isAprovando}
                onClick={() => aprovarOrdem({ id: idNum, request: { aprovado: false } })}>
                <XCircle className="h-4 w-4" />Rejeitar
              </Button>
            </>
          ) : status === 'Aprovada' ? (
            <Button variant="outline" className="gap-2 text-cyan-600 border-cyan-600"
              disabled={isEnviando}
              onClick={() => enviarFornecedor(idNum)}>
              <Truck className="h-4 w-4" />Enviar ao Fornecedor
            </Button>
          ) : (status === 'EnviadaFornecedor' || status === 'ConfirmadaFornecedor' || status === 'ParcialmenteRecebida') ? (
            <Button variant="outline" className="gap-2"
              onClick={() => navigate(`/compras/ordens/${idNum}/receber`)}>
              <Package className="h-4 w-4" />Registrar Recebimento
            </Button>
          ) : null}
        </div>
      </div>

      {/* Cabeçalho */}
      <Card>
        <CardHeader><CardTitle>Identificação</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="oc-fornecedor">Fornecedor *</Label>
              <FornecedorCombobox
                id="oc-fornecedor"
                value={fornecedorId}
                nomeInicial={detalhe?.fornecedorId === fornecedorId ? detalhe?.nomeFornecedor : null}
                onChange={id => setFornecedorId(id ?? 0)}
                disabled={!podeEditar}
              />
            </div>
            <div className="space-y-2">
              <Label>Entrega Prevista</Label>
              <Input type="date" value={dtEntregaPrevista} onChange={e => setDtEntregaPrevista(e.target.value)}
                disabled={!podeEditar} />
            </div>
            <div className="space-y-2">
              <Label>Prazo de Pagamento (dias)</Label>
              <Input type="number" value={prazoPagamento} onChange={e => setPrazoPagamento(Number(e.target.value))}
                disabled={!podeEditar} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={2}
              disabled={!podeEditar} />
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Itens</CardTitle>
          {podeEditar && (
            <Button size="sm" variant="outline" className="gap-1" onClick={addItem}>
              <Plus className="h-4 w-4" />Adicionar Item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[260px]">Produto</TableHead>
                <TableHead>Quantidade</TableHead>
                <TableHead>Custo Unit.</TableHead>
                <TableHead>Desconto</TableHead>
                <TableHead className="text-right">Total</TableHead>
                {podeEditar && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    Adicione ao menos um item
                  </TableCell>
                </TableRow>
              ) : (
                itens.map(item => (
                  <TableRow key={item._key}>
                    <TableCell>
                      <ProdutoSearchCombobox
                        value={item.produtoId || undefined}
                        onChange={pid => updateItem(item._key, 'produtoId', pid)}
                        placeholder="Buscar produto..."
                        disabled={!podeEditar} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.0001" value={item.quantidade}
                        onChange={e => updateItem(item._key, 'quantidade', Number(e.target.value))}
                        disabled={!podeEditar} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.0001" value={item.custoUnitario}
                        onChange={e => updateItem(item._key, 'custoUnitario', Number(e.target.value))}
                        disabled={!podeEditar} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" value={item.descontoItem}
                        onChange={e => updateItem(item._key, 'descontoItem', Number(e.target.value))}
                        disabled={!podeEditar} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {(item.quantidade * item.custoUnitario - item.descontoItem).toLocaleString('pt-BR', {
                        style: 'currency', currency: 'BRL',
                      })}
                    </TableCell>
                    {podeEditar && (
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removeItem(item._key)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Totais */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Frete (R$)</Label>
              <Input type="number" step="0.01" value={frete} onChange={e => setFrete(Number(e.target.value))}
                disabled={!podeEditar} />
            </div>
            <div className="space-y-2">
              <Label>Desconto Geral (R$)</Label>
              <Input type="number" step="0.01" value={desconto} onChange={e => setDesconto(Number(e.target.value))}
                disabled={!podeEditar} />
            </div>
            <div className="space-y-2">
              <Label>Outras Despesas (R$)</Label>
              <Input type="number" step="0.01" value={outrasDespesas} onChange={e => setOutrasDespesas(Number(e.target.value))}
                disabled={!podeEditar} />
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Valor Total</p>
              <p className="text-2xl font-bold">
                {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {podeEditar && (
        <div className="flex gap-3">
          <Button onClick={handleSalvar} disabled={isSaving || itens.length === 0 || !fornecedorId} className="gap-2">
            <Save className="h-4 w-4" />{isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/compras/ordens')}>Cancelar</Button>
        </div>
      )}
    </div>
  );
};

export default OrdemCompraForm;
